import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AssessmentTable = ({ assessments, onAssessmentSelect, selectedAssessment }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState('triggerDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAssessments = [...assessments]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    if (sortField === 'triggerDate' || sortField === 'dueDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (sortField === 'gapPercentage') {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'open': { color: 'bg-warning text-warning-foreground', label: 'Open' },
      'in-review': { color: 'bg-primary text-primary-foreground', label: 'In Review' },
      'remediation': { color: 'bg-accent text-accent-foreground', label: 'Remediation' },
      'closed': { color: 'bg-muted text-muted-foreground', label: 'Closed' }
    };

    const config = statusConfig?.[status] || statusConfig?.['open'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'critical': { color: 'bg-destructive text-destructive-foreground', icon: 'AlertTriangle' },
      'high': { color: 'bg-warning text-warning-foreground', icon: 'AlertCircle' },
      'medium': { color: 'bg-accent text-accent-foreground', icon: 'Info' },
      'low': { color: 'bg-muted text-muted-foreground', icon: 'Minus' }
    };

    const config = priorityConfig?.[priority] || priorityConfig?.['medium'];
    return (
      <div className="flex items-center space-x-1">
        <Icon name={config?.icon} size={14} />
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
          {priority?.charAt(0)?.toUpperCase() + priority?.slice(1)}
        </span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-smooth"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <SortableHeader field="caseId">Case ID</SortableHeader>
              <SortableHeader field="department">{t('jointAssessments.table.colDepartment')}</SortableHeader>
              <SortableHeader field="gapPercentage">Gap %</SortableHeader>
              <SortableHeader field="triggerDate">Trigger Date</SortableHeader>
              <SortableHeader field="dueDate">{t('jointAssessments.table.colDueDate')}</SortableHeader>
              <SortableHeader field="assignee">{t('jointAssessments.table.colAssignee')}</SortableHeader>
              <SortableHeader field="priority">{t('jointAssessments.table.colPriority')}</SortableHeader>
              <SortableHeader field="status">Status</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedAssessments?.map((assessment) => (
              <tr 
                key={assessment?.id}
                className={`hover:bg-muted transition-smooth cursor-pointer ${
                  selectedAssessment?.id === assessment?.id ? 'bg-muted' : ''
                }`}
                onClick={() => onAssessmentSelect(assessment)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">
                    {assessment?.caseId}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{assessment?.department}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-destructive">
                    {assessment?.gapPercentage}%
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-muted-foreground">
                    {formatDate(assessment?.triggerDate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-muted-foreground">
                    {formatDate(assessment?.dueDate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs text-primary-foreground font-medium">
                        {assessment?.assignee?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div className="text-sm text-foreground">{assessment?.assignee}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPriorityBadge(assessment?.priority)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(assessment?.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onAssessmentSelect(assessment);
                    }}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {sortedAssessments?.map((assessment) => (
          <div
            key={assessment?.id}
            className={`border border-border rounded-lg p-4 cursor-pointer transition-smooth ${
              selectedAssessment?.id === assessment?.id 
                ? 'border-primary bg-primary/5' :'hover:border-muted-foreground'
            }`}
            onClick={() => onAssessmentSelect(assessment)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-medium text-foreground">{assessment?.caseId}</h4>
                <p className="text-sm text-muted-foreground">{assessment?.department}</p>
              </div>
              <div className="flex flex-col items-end space-y-2">
                {getStatusBadge(assessment?.status)}
                {getPriorityBadge(assessment?.priority)}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Gap:</span>
                <span className="ml-2 font-medium text-destructive">
                  {assessment?.gapPercentage}%
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Assignee:</span>
                <span className="ml-2 text-foreground">{assessment?.assignee}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Triggered:</span>
                <span className="ml-2 text-foreground">
                  {formatDate(assessment?.triggerDate)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Due:</span>
                <span className="ml-2 text-foreground">
                  {formatDate(assessment?.dueDate)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {assessments?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileSearch" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Assessments Found</h3>
          <p className="text-muted-foreground">
            No joint assessments match your current filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default AssessmentTable;